import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const items = [{
  emoji: '📖',
  title: '+150 Sesiones',
  desc: 'Los 6 Volúmenes del Método. Guía completa con explicaciones, dibujos técnicos y progresiones.',
  color: 'bg-blue-500',
  shadow: 'shadow-[0_0_15px_rgba(59,130,246,0.5)]'
}, {
  emoji: '📋',
  title: '15 Pizarras Tácticas',
  desc: 'Grillas de rotación y sistemas visuales listos para mostrar en cancha.',
  color: 'bg-green-500',
  shadow: 'shadow-[0_0_15px_rgba(34,197,94,0.5)]'
}, {
  emoji: '💪',
  title: '101 Drills Explosivos',
  desc: 'Secuencias dinámicas que integran técnica individual y táctica colectiva.',
  color: 'bg-orange-500',
  shadow: 'shadow-[0_0_15px_rgba(249,115,22,0.5)]'
}, {
  emoji: '🗂️',
  title: 'Guía de Scouting Express',
  desc: 'Mapas visuales para analizar rivales desde la grada con tu celular.',
  color: 'bg-purple-500',
  shadow: 'shadow-[0_0_15px_rgba(168,85,247,0.5)]'
}];
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/1HCf3iEvNnupyD7egmk978/Captura_de_pantalla_2026-04-20_a_las_8.54.35_p._m..png',
  alt: 'Sesión 27 - Circulación de Balón'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/49VVrvTwDoscnyrk2kmxB9/Captura_de_pantalla_2026-04-20_a_las_8.56.24_p._m..png',
  alt: 'Ficha de Evaluación Táctica'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/43P46r69kcx3iV46QPVtoq/Captura_de_pantalla_2026-04-20_a_las_9.04.10_p._m..png',
  alt: 'Sesión HIIT Específico'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/fq7cXDG3TqAiG5NJisfqED/Captura_de_pantalla_2026-04-20_a_las_9.13.06_p._m..png',
  alt: 'Drills 24-27 Recepción'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/v88Cm4MmDbsfvvM6JngW8L/Captura_de_pantalla_2026-04-20_a_las_9.14.00_p._m..png',
  alt: 'La Planilla Maestra'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const handleScroll = () => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const center = container.scrollLeft + container.clientWidth / 2;
    let minDistance = Infinity;
    let active = 0;
    Array.from(container.children).forEach((child, index) => {
      const childCenter = (child as HTMLElement).offsetLeft + (child as HTMLElement).clientWidth / 2;
      const distance = Math.abs(center - childCenter);
      if (distance < minDistance) {
        minDistance = distance;
        active = index;
      }
    });
    setActiveIndex(active);
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < carouselImages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="w-full bg-bg-dark py-20 px-4" data-id="element-29">
      <div className="max-w-5xl mx-auto" data-id="element-30">
        <div className="text-center mb-14" data-id="element-31">
          <span className="inline-block text-primary font-bold text-sm tracking-widest uppercase mb-3" data-id="element-32">
            📦 ¿Qué recibes en tu correo?
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-4 tracking-tight" data-id="element-33">
            <span className="text-primary" data-id="element-34">300+ páginas</span> de material listo
            para usar.
          </h2>
          <p className="text-text-sec text-lg sm:text-xl max-w-2xl mx-auto" data-id="element-35">
            PDF optimizados para usar digitalmente o imprimir.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-36">
          {items.map((item, i) => <div key={i} className="bg-bg-alt rounded-2xl border border-border-light overflow-hidden flex flex-col relative group hover:-translate-y-1 transition-transform duration-300" data-id="element-37">
              <div className={`${item.color} h-1.5 w-full ${item.shadow}`} data-id="element-38" />
              <div className="flex-1 flex flex-col p-8 relative" data-id="element-39">
                <div className={`absolute inset-0 ${item.color} opacity-[0.03] group-hover:opacity-[0.08] transition-opacity`} data-id="element-40"></div>

                <span className="text-5xl mb-5 block drop-shadow-2xl relative z-10 group-hover:scale-110 transition-transform duration-300" data-id="element-41">
                  {item.emoji}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-text-main leading-snug relative z-10 mb-3" data-id="element-42">
                  {item.title}
                </h3>
                <p className="text-text-sec text-base leading-relaxed relative z-10" data-id="element-43">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>

        {/* Carousel Section */}
        <div className="mt-24 relative" data-id="element-44">
          <div className="text-center mb-10" data-id="element-45">
            <h3 className="text-2xl sm:text-3xl font-bold text-text-main inline-flex items-center gap-3" data-id="element-46">
              <span data-id="element-47">📸</span> Mira el material por dentro
            </h3>
            <p className="text-text-sec text-sm mt-3 flex items-center justify-center gap-2" data-id="element-48">
              <span className="inline-flex items-center gap-1.5 sm:hidden" data-id="element-49">
                <ChevronLeft className="w-4 h-4 animate-pulse" data-id="element-50" />
                Desliza para ver más
                <ChevronRight className="w-4 h-4 animate-pulse" data-id="element-51" />
              </span>
              <span className="text-text-sec/60 text-xs font-medium" data-id="element-52">
                {activeIndex + 1} / {carouselImages.length}
              </span>
            </p>
          </div>

          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-53">
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-dark/80 hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main backdrop-blur-sm transition-all shadow-lg ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Anterior" data-id="element-54">
              <ChevronLeft className="w-6 h-6" data-id="element-55" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-dark/80 hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main backdrop-blur-sm transition-all shadow-lg ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Siguiente" data-id="element-56">
              <ChevronRight className="w-6 h-6" data-id="element-57" />
            </button>

            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-175px)] pb-8 pt-4" data-id="element-58">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[350px] bg-bg-alt border border-border-light rounded-2xl overflow-hidden p-2 sm:p-3 flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.3)] transition-transform duration-300 ${activeIndex === i ? 'scale-100 opacity-100' : 'scale-95 opacity-50'}`} data-id="element-59">
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[400px] sm:max-h-[450px] object-contain rounded-xl bg-white/5" data-id="element-60" />
                </div>)}
            </div>
          </div>

          <div className="flex justify-center gap-2.5 mt-2" data-id="element-61">
            {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className={`h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec w-2'}`} aria-label={`Ir a imagen ${i + 1}`} data-id="element-62" />)}
          </div>
        </div>
      </div>
    </section>;
}